import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Modal, Button } from 'react-bootstrap';
import axios from 'axios';
import { getProductVariants } from '../../utils/ecommerceHelper';
import { getArticleImageSlides } from '../../utils/imageHelper';

// Componente para tarjeta individual de Artículo Relacionado
const RelatedProductCard = ({ product, canSeePrice, getPrice, onClick }) => {
  const slides = useMemo(() => {
    if (!product) return [];
    return getArticleImageSlides(product);
  }, [product]);

  const [candidateIdx, setCandidateIdx] = useState(0);
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    setCandidateIdx(0);
    setImgError(false);
  }, [product]);

  const candidates = slides.length > 0 && slides[0].candidates ? slides[0].candidates : [];
  const currentUrl = candidates[candidateIdx] || null;

  const displayPrice = useMemo(() => {
    if (typeof getPrice === 'function') {
      return getPrice(product);
    }
    const offerPrice = Number(product?.i_preant ?? product?.I_PREANT ?? 0);
    if (offerPrice > 0) return offerPrice;
    return Number(product?.i_lista || product?.i_conta || 0);
  }, [product, getPrice]);

  const isOffer = Number(product?.i_preant ?? product?.I_PREANT ?? 0) > 0;

  return (
    <div
      className="card h-100 border rounded-3 p-2 bg-white shadow-xs position-relative cursor-pointer transition-all ecom-related-card"
      style={{
        cursor: 'pointer',
        transition: 'all 0.18s ease-in-out'
      }}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      title={`Ver detalle de ${product.d_artic}`}
    >
      {/* Imagen */}
      <div
        className="rounded-2 bg-light mb-2 d-flex align-items-center justify-content-center overflow-hidden position-relative border"
        style={{ height: '115px' }}
      >
        {!imgError && currentUrl ? (
          <img
            src={currentUrl}
            alt={product.d_artic}
            className="w-100 h-100 object-fit-contain p-1"
            onError={() => {
              if (candidateIdx + 1 < candidates.length) {
                setCandidateIdx(prev => prev + 1);
              } else {
                setImgError(true);
              }
            }}
          />
        ) : (
          <div className="text-muted d-flex flex-column align-items-center">
            <i className="bi bi-box-seam fs-3 opacity-50"></i>
          </div>
        )}

        {isOffer && (
          <span
            className="badge bg-danger position-absolute top-0 start-0 m-1 px-1.5 py-0.5 fw-bold"
            style={{ fontSize: '0.62rem' }}
          >
            OFERTA
          </span>
        )}
      </div>

      {/* Categoría / Subfamilia / Rubro */}
      {(product.d_famil || product.d_rubro) && (
        <span
          className="badge bg-light text-secondary border text-truncate mb-1 px-1.5 py-0.5 text-uppercase"
          style={{ fontSize: '0.65rem', maxWidth: '100%', textAlign: 'left', display: 'block' }}
        >
          {product.d_famil || product.d_rubro}
        </span>
      )}

      {/* Nombre */}
      <div
        className="fw-bold text-dark mb-1"
        style={{
          fontSize: '0.78rem',
          lineHeight: '1.25',
          height: '2.5em',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden'
        }}
      >
        {product.d_artic}
      </div>

      {/* Precio */}
      <div className="mt-auto pt-1 d-flex align-items-center justify-content-between">
        {canSeePrice ? (
          <span className={`fw-bolder ${isOffer ? 'text-danger' : 'text-primary'}`} style={{ fontSize: '0.82rem' }}>
            ${Number(displayPrice || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        ) : (
          <span className="small text-muted" style={{ fontSize: '0.7rem' }}>
            Ver precio
          </span>
        )}
        <i className="bi bi-chevron-right text-muted small"></i>
      </div>
    </div>
  );
};

const EcommerceProductDetail = ({
  show,
  onHide,
  onClose,
  product: initialProduct,
  canSeePrice,
  canSeePrices,
  price,
  regularPrice,
  priceListLabel,
  isLoggedIn = false,
  onAddToCart,
  onLoginClick,
  allArticles = [],
  onSelectProduct,
  storeConfig = null,
  getPriceForArticle = null
}) => {
  const [internalProduct, setInternalProduct] = useState(initialProduct);
  const modalBodyRef = useRef(null);

  useEffect(() => {
    setInternalProduct(initialProduct);
  }, [initialProduct]);

  const product = internalProduct || initialProduct;
  const handleClose = onHide || onClose;
  const effectiveCanSeePrice = canSeePrice !== undefined ? canSeePrice : (canSeePrices !== undefined ? canSeePrices : false);

  const effectivePrice = useMemo(() => {
    if (typeof getPriceForArticle === 'function' && product) {
      return getPriceForArticle(product);
    }
    if (product && product !== initialProduct) {
      const offer = Number(product?.i_preant ?? product?.I_PREANT ?? 0);
      if (offer > 0) return offer;
      return Number(product?.i_lista || product?.i_conta || 0);
    }
    return price;
  }, [product, initialProduct, getPriceForArticle, price]);

  const effectiveRegularPrice = useMemo(() => {
    if (product && product !== initialProduct) {
      return Number(product?.i_lista || product?.i_conta || 0);
    }
    return regularPrice;
  }, [product, initialProduct, regularPrice]);

  // Configuración de artículos relacionados
  const relatedConfig = useMemo(() => {
    return storeConfig?.relatedProducts || {
      enabled: true,
      title: 'Artículos Relacionados',
      subtitle: 'Productos similares que también te pueden interesar',
      criteria: 'subfamily_or_rubro',
      maxItems: 6
    };
  }, [storeConfig]);

  const [fetchedRelated, setFetchedRelated] = useState([]);

  // Artículos relacionados calculados según el criterio configurado
  const relatedProducts = useMemo(() => {
    if (!product || relatedConfig.enabled === false) return [];
    const maxItems = Number(relatedConfig.maxItems) || 6;
    const criteria = relatedConfig.criteria || 'subfamily_or_rubro';

    if (criteria === 'custom') {
      const customList = Array.isArray(relatedConfig.customArticles) ? relatedConfig.customArticles : [];
      return customList.filter(a => {
        const code = a.c_artic || a.id || a;
        return String(code) !== String(product.c_artic);
      }).slice(0, maxItems);
    }

    const pool = Array.isArray(allArticles) && allArticles.length > 0 ? allArticles : fetchedRelated;
    const candidates = pool.filter(a => {
      if (!a) return false;
      const code = a.c_artic || a.id;
      if (String(code) === String(product.c_artic)) return false;
      if (Number(a.c_situa) === 9) return false;
      return true;
    });

    if (criteria === 'same_subfamily') {
      return candidates.filter(a => (
        a.c_famil && product.c_famil && String(a.c_famil) === String(product.c_famil)
      )).slice(0, maxItems);
    }

    if (criteria === 'same_rubro') {
      return candidates.filter(a => (
        a.c_rubro && product.c_rubro && String(a.c_rubro) === String(product.c_rubro)
      )).slice(0, maxItems);
    }

    // subfamily_or_rubro: prioridad misma subfamilia y completa con mismo rubro
    const sameSub = candidates.filter(a => (
      a.c_famil && product.c_famil && String(a.c_famil) === String(product.c_famil)
    ));
    const sameRubro = candidates.filter(a => (
      a.c_rubro && product.c_rubro && String(a.c_rubro) === String(product.c_rubro) && !sameSub.includes(a)
    ));

    return [...sameSub, ...sameRubro].slice(0, maxItems);
  }, [product, relatedConfig, allArticles, fetchedRelated]);

  // Si no hay suficientes candidatos locales y el criterio es automático, buscar en la API por rubro
  useEffect(() => {
    if (!product || relatedConfig.enabled === false || relatedConfig.criteria === 'custom') {
      return;
    }
    const hasEnoughLocal = Array.isArray(allArticles) && allArticles.some(a => (
      String(a.c_artic) !== String(product.c_artic) && (
        (a.c_famil && String(a.c_famil) === String(product.c_famil)) ||
        (a.c_rubro && String(a.c_rubro) === String(product.c_rubro))
      )
    ));

    if (!hasEnoughLocal && product.c_rubro) {
      const compId = storeConfig?.company_id || (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') ? 5 : null);
      axios.get('/api/articulos/', {
        params: {
          rubro: product.c_rubro,
          page: 1,
          page_size: Math.max(Number(relatedConfig.maxItems) || 6, 8) + 2,
          company: compId
        },
        headers: compId ? { 'X-Company-ID': String(compId) } : {}
      }).then(res => {
        const items = Array.isArray(res.data) ? res.data : (res.data?.results || []);
        setFetchedRelated(items);
      }).catch(err => {
        console.warn('Error cargando artículos relacionados de respaldo:', err);
      });
    }
  }, [product, relatedConfig, allArticles, storeConfig]);

  const handleSelectRelated = (newArticle) => {
    setInternalProduct(newArticle);
    if (typeof onSelectProduct === 'function') {
      onSelectProduct(newArticle);
    }
    if (modalBodyRef.current) {
      modalBodyRef.current.scrollTop = 0;
    }
  };
  const talles = useMemo(() => {
    return Array.isArray(product?.talles) ? product.talles : [];
  }, [product]);

  const hasTalles = talles.length > 0;
  const [selectedSize, setSelectedSize] = useState(null);

  const variants = useMemo(() => {
    return getProductVariants(product);
  }, [product]);

  const slides = useMemo(() => {
    if (!product) return [];
    return getArticleImageSlides(product);
  }, [product]);

  const [selectedVariant, setSelectedVariant] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [isAdded, setIsAdded] = useState(false);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [candidateIndices, setCandidateIndices] = useState({});
  const [failedSlides, setFailedSlides] = useState({});

  // Estados para efecto Zoom (Lupa) y Lightbox (Imagen en grande)
  const [isHoverZoom, setIsHoverZoom] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 50, y: 50 });
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  useEffect(() => {
    if (variants.length > 0) {
      setSelectedVariant(variants[0]);
    }
    if (hasTalles) {
      const withStock = talles.find(t => Number(t.stock || 0) > 0);
      setSelectedSize(withStock || talles[0]);
    } else {
      setSelectedSize(null);
    }
    setQuantity(1);
    setIsAdded(false);
    setCurrentSlideIndex(0);
    setCandidateIndices({});
    setFailedSlides({});
    setIsHoverZoom(false);
    setIsLightboxOpen(false);
  }, [product, variants, hasTalles, talles]);

  const effectiveStock = useMemo(() => {
    if (hasTalles && selectedSize) {
      return Number(selectedSize.stock || 0);
    }
    return Number(product?.q_stock || 0);
  }, [hasTalles, selectedSize, product?.q_stock]);

  const hasStock = effectiveStock > 0;

  const validSlides = useMemo(() => {
    return slides.filter(s => !failedSlides[s.index]);
  }, [slides, failedSlides]);

  const totalValidSlides = validSlides.length;
  const currentSlide = slides[currentSlideIndex] || null;

  const getSlideUrl = useCallback((slide) => {
    if (!slide || !slide.candidates || slide.candidates.length === 0) return null;
    if (failedSlides[slide.index]) return null;
    const cIndex = candidateIndices[slide.index] || 0;
    if (cIndex >= slide.candidates.length) return null;
    return slide.candidates[cIndex] || null;
  }, [candidateIndices, failedSlides]);

  const currentUrl = getSlideUrl(currentSlide);
  const hasImage = Boolean(currentUrl);

  const handlePrev = useCallback(() => {
    if (validSlides.length <= 1) return;
    const currentPos = validSlides.findIndex(s => s.index === currentSlide?.index);
    const newPos = currentPos > 0 ? currentPos - 1 : validSlides.length - 1;
    const target = validSlides[newPos];
    if (target) {
      const origIdx = slides.findIndex(s => s.index === target.index);
      if (origIdx !== -1) setCurrentSlideIndex(origIdx);
    }
  }, [validSlides, currentSlide, slides]);

  const handleNext = useCallback(() => {
    if (validSlides.length <= 1) return;
    const currentPos = validSlides.findIndex(s => s.index === currentSlide?.index);
    const newPos = currentPos < validSlides.length - 1 ? currentPos + 1 : 0;
    const target = validSlides[newPos];
    if (target) {
      const origIdx = slides.findIndex(s => s.index === target.index);
      if (origIdx !== -1) setCurrentSlideIndex(origIdx);
    }
  }, [validSlides, currentSlide, slides]);

  // Navegación con teclado
  useEffect(() => {
    if (!show || totalValidSlides <= 1) return;
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        handlePrev();
      } else if (e.key === 'ArrowRight') {
        handleNext();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [show, totalValidSlides, handlePrev, handleNext]);

  const handleImageError = (slideIndex) => {
    const slide = slides.find(s => s.index === slideIndex);
    if (!slide) return;

    setCandidateIndices(prev => {
      const currentCIndex = prev[slideIndex] || 0;
      if (currentCIndex < slide.candidates.length - 1) {
        return { ...prev, [slideIndex]: currentCIndex + 1 };
      }
      // Se agotaron todos los candidatos de esta foto -> marcar como fallida
      setFailedSlides(f => {
        const nextFailed = { ...f, [slideIndex]: true };
        const remaining = slides.filter(s => !nextFailed[s.index]);
        if (remaining.length > 0) {
          const nextIdx = slides.findIndex(s => s.index === remaining[0].index);
          if (nextIdx !== -1) {
            setCurrentSlideIndex(nextIdx);
          }
        }
        return nextFailed;
      });
      return prev;
    });
  };

  if (!product) return null;

  const brandLabel = (product?.d_marca || product?.c_marca || '').trim();
  const hasBrand = Boolean(brandLabel && !['0', '00', '000', '-'].includes(brandLabel));

  const rubroLabel = (product?.d_rubro || '').trim();
  const hasRubro = Boolean(rubroLabel && !rubroLabel.toLowerCase().startsWith('rubro'));

  const familiaLabel = (product?.d_famil || '').trim();
  const hasFamilia = Boolean(familiaLabel && !['0', '00', '000', '-'].includes(familiaLabel) && familiaLabel.toLowerCase() !== rubroLabel.toLowerCase());

  const handleAddToCart = () => {
    if (!hasStock) return;
    if (effectiveStock > 0 && quantity > effectiveStock) return;
    onAddToCart({
      ...product,
      selectedVariant,
      selectedSize,
      quantity,
      maxStock: effectiveStock > 0 ? effectiveStock : undefined,
      unitPrice: effectivePrice,
      canSeePrice: effectiveCanSeePrice
    });
    setIsAdded(true);
    setTimeout(() => {
      setIsAdded(false);
      if (typeof handleClose === 'function') {
        handleClose();
      }
    }, 900);
  };

  const handleMouseMove = (e) => {
    if (!hasImage) return;
    const rect = e.currentTarget.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) {
      const x = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
      const y = Math.max(0, Math.min(100, ((e.clientY - rect.top) / rect.height) * 100));
      setMousePos({ x, y });
    }
  };

  const handleMouseEnter = () => {
    if (hasImage) {
      setIsHoverZoom(true);
    }
  };

  const handleMouseLeave = () => {
    setIsHoverZoom(false);
    setMousePos({ x: 50, y: 50 });
  };

  const handleImageClick = () => {
    if (hasImage) {
      setIsLightboxOpen(true);
    }
  };

  return (
    <>
      <Modal show={show} onHide={handleClose} size="xl" centered className="rounded-4">
        {/* Botón de Cierre Personalizado con Amplio Rango de Clic */}
        <button
          type="button"
          className="ecom-modal-close-btn"
          onClick={handleClose}
          title="Cerrar"
          aria-label="Cerrar modal"
        >
          <i className="bi bi-x-lg"></i>
        </button>

        <Modal.Body ref={modalBodyRef} className="p-4 pt-4 position-relative" style={{ maxHeight: '90vh', overflowY: 'auto' }}>
          <div className="row g-4 align-items-start">
            
            {/* Imagen / Carrusel del Producto */}
            <div className="col-12 col-md-5">
              <div 
                className="position-relative bg-light rounded-4 p-3 text-center border d-flex align-items-center justify-content-center overflow-hidden ecom-detail-img-wrapper" 
                style={{ 
                  minHeight: '280px', 
                  maxHeight: '340px',
                  cursor: hasImage ? 'zoom-in' : 'default',
                  userSelect: 'none'
                }}
                onMouseMove={handleMouseMove}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onClick={handleImageClick}
                role={hasImage ? "button" : undefined}
                tabIndex={hasImage ? 0 : undefined}
                onKeyDown={(e) => {
                  if (hasImage && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    setIsLightboxOpen(true);
                  }
                }}
                title={hasImage ? "Hacé clic para ver la imagen en grande" : undefined}
              >
                {hasImage ? (
                  <>
                    <img 
                      key={`${currentSlideIndex}_${currentUrl}`}
                      src={currentUrl} 
                      alt={`${product.d_artic} - Foto ${currentSlideIndex + 1}`} 
                      className="img-fluid rounded-3" 
                      style={{ 
                        maxHeight: '260px',
                        maxWidth: '100%',
                        objectFit: 'contain', 
                        transform: isHoverZoom ? 'scale(2.2)' : 'scale(1)',
                        transformOrigin: `${mousePos.x}% ${mousePos.y}%`,
                        transition: isHoverZoom ? 'transform 0.08s ease-out' : 'transform 0.25s ease-out',
                        pointerEvents: 'none'
                      }} 
                      onError={() => handleImageError(currentSlide.index)}
                    />

                    {/* Badge / Indicador de Lupa flotante */}
                    <div 
                      className={`position-absolute top-0 end-0 m-2 px-2 py-1 rounded-pill bg-dark bg-opacity-75 text-white small d-flex align-items-center gap-1 shadow-sm transition-all ${
                        isHoverZoom ? 'opacity-100 scale-105' : 'opacity-75'
                      }`}
                      style={{ fontSize: '0.72rem', zIndex: 6, pointerEvents: 'none' }}
                    >
                      <i className="bi bi-search"></i>
                      <span>{isHoverZoom ? 'Zoom' : 'Ampliar'}</span>
                    </div>

                    {/* Flechas de Navegación del Carrusel */}
                    {totalValidSlides > 1 && (
                      <>
                        <button
                          type="button"
                          className="btn btn-dark position-absolute top-50 start-0 translate-middle-y ms-2 rounded-circle d-flex align-items-center justify-content-center shadow-sm"
                          style={{ width: '36px', height: '36px', opacity: 0.85, zIndex: 10 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePrev();
                          }}
                          title="Foto anterior"
                          aria-label="Foto anterior"
                        >
                          <i className="bi bi-chevron-left"></i>
                        </button>

                        <button
                          type="button"
                          className="btn btn-dark position-absolute top-50 end-0 translate-middle-y me-2 rounded-circle d-flex align-items-center justify-content-center shadow-sm"
                          style={{ width: '36px', height: '36px', opacity: 0.85, zIndex: 10 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleNext();
                          }}
                          title="Foto siguiente"
                          aria-label="Foto siguiente"
                        >
                          <i className="bi bi-chevron-right"></i>
                        </button>

                        {/* Contador de Fotos */}
                        <div 
                          className="position-absolute bottom-0 start-50 translate-middle-x mb-2 px-2.5 py-0.5 rounded-pill bg-dark bg-opacity-75 text-white small fw-bold"
                          style={{ fontSize: '0.72rem', zIndex: 6 }}
                        >
                          <i className="bi bi-camera me-1"></i>
                          {validSlides.findIndex(s => s.index === currentSlide?.index) + 1} / {totalValidSlides}
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  <div className="text-muted opacity-50 py-4">
                    <i className="bi bi-box-seam display-1"></i>
                    <p className="small text-uppercase mt-2 mb-0 fw-semibold">Sin imagen disponible</p>
                  </div>
                )}
              </div>

            {/* Tira de Miniaturas debajo de la foto */}
            {totalValidSlides > 1 && (
              <div className="d-flex align-items-center justify-content-center gap-2 mt-2 px-1 py-1 bg-white rounded-3 border overflow-auto">
                {validSlides.map((slide) => {
                  const thumbUrl = getSlideUrl(slide);
                  if (!thumbUrl) return null;
                  const isActive = slide.index === currentSlide?.index;
                  return (
                    <button
                      key={slide.index}
                      type="button"
                      className={`btn p-0.5 rounded-2 border ${
                        isActive ? 'border-primary border-2 shadow-sm' : 'border-light-subtle opacity-75'
                      }`}
                      style={{
                        width: '50px',
                        height: '50px',
                        background: '#f8fafc',
                        cursor: 'pointer',
                        transform: isActive ? 'scale(1.05)' : 'scale(1)',
                        transition: 'all 0.2s ease'
                      }}
                      onClick={() => {
                        const origIdx = slides.findIndex(s => s.index === slide.index);
                        if (origIdx !== -1) setCurrentSlideIndex(origIdx);
                      }}
                      title={`Ver foto ${validSlides.indexOf(slide) + 1}`}
                    >
                      <img
                        src={thumbUrl}
                        alt={`Miniatura ${validSlides.indexOf(slide) + 1}`}
                        className="w-100 h-100 rounded-1"
                        style={{ objectFit: 'cover' }}
                        onError={() => handleImageError(slide.index)}
                      />
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Información del Producto */}
          <div className="col-12 col-md-7">
            {/* Detección de Oferta Especial */}
            {(() => {
              const previousPrice = Number(product?.i_preant ?? product?.I_PREANT ?? 0);
              const isOffer = previousPrice > 0;
              return (
                <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
                  {isOffer && (
                    <span className="badge bg-danger text-white border border-danger px-2.5 py-1 text-uppercase fw-bold shadow-xs d-inline-flex align-items-center gap-1">
                      <i className="bi bi-tag-fill"></i> OFERTA ESPECIAL
                    </span>
                  )}
                  {hasRubro && (
                    <span className="badge bg-primary-subtle text-primary border border-primary-subtle px-2 py-1">
                      {rubroLabel}
                    </span>
                  )}
                  {hasFamilia && (
                    <span className="badge bg-light text-dark border px-2 py-1 fw-semibold" style={{ fontSize: '0.75rem' }}>
                      {familiaLabel}
                    </span>
                  )}
                  {hasBrand && (
                    <span className="badge bg-light text-dark border px-2 py-1 text-uppercase fw-semibold" style={{ fontSize: '0.72rem' }}>
                      {brandLabel}
                    </span>
                  )}
                  {isLoggedIn && (
                    hasStock ? (
                      <span className="badge bg-success-subtle text-success border border-success-subtle px-2 py-1 fw-semibold d-inline-flex align-items-center gap-1">
                        <i className="bi bi-box-seam"></i>
                        <span>Stock: {Number(effectiveStock).toLocaleString('es-AR')} un.{hasTalles && selectedSize ? ` (Talle ${selectedSize.nombre})` : ''}</span>
                      </span>
                    ) : (
                      <span className="badge bg-danger-subtle text-danger border border-danger-subtle px-2 py-1 fw-bold d-inline-flex align-items-center gap-1">
                        <i className="bi bi-x-circle-fill"></i>
                        <span>Sin stock{hasTalles && selectedSize ? ` (Talle ${selectedSize.nombre})` : ''}</span>
                      </span>
                    )
                  )}
                </div>
              );
            })()}
            <h4 className="fw-bold text-dark mb-2">{product.d_artic}</h4>

            {/* Sección de Descripción del Artículo (en caso de tenerla) */}
            {(product.d_arti2 || product.d_tecni || product.d_obser || (product.description && product.description !== product.d_artic) || product.notes) && (
              <div className="p-3 bg-light rounded-3 mb-3 border">
                <div className="d-flex align-items-center gap-1.5 mb-1.5 text-primary fw-bold text-uppercase" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>
                  <i className="bi bi-card-text fs-6"></i>
                  <span>Descripción del artículo</span>
                </div>
                {product.d_arti2 && (
                  <p className="text-secondary small mb-1 lh-base fw-medium">
                    {product.d_arti2}
                  </p>
                )}
                {product.d_tecni && (
                  <p className="text-secondary small mb-1 lh-base">
                    {product.d_tecni}
                  </p>
                )}
                {product.description && product.description !== product.d_artic && product.description !== product.d_arti2 && (
                  <p className="text-secondary small mb-1 lh-base">
                    {product.description}
                  </p>
                )}
                {(product.d_obser || product.notes) && (
                  <div className="text-muted small mt-2 pt-1 border-top fst-italic" style={{ fontSize: '0.78rem' }}>
                    <i className="bi bi-info-circle me-1"></i>
                    {product.d_obser || product.notes}
                  </div>
                )}
              </div>
            )}

            <div className="d-flex flex-wrap gap-3 p-3 bg-light rounded-3 mb-3 small">
              <div>
                <span className="text-muted d-block">Código:</span>
                <strong className="text-dark font-monospace">{product.c_artic}</strong>
              </div>
              {product.c_barra && (
                <div>
                  <span className="text-muted d-block">Cód. Barras (EAN):</span>
                  <strong className="text-dark font-monospace">{product.c_barra}</strong>
                </div>
              )}
              {Number(product.q_unxbu || 0) > 0 ? (
                <div>
                  <span className="text-muted d-block">Unidades x Bulto:</span>
                  <strong className="text-dark">{product.q_unxbu} un.</strong>
                </div>
              ) : null}
              {isLoggedIn && (
                <div>
                  <span className="text-muted d-block">Disponibilidad:</span>
                  {hasStock ? (
                    <strong className="text-success d-inline-flex align-items-center gap-1">
                      <i className="bi bi-box-seam"></i>
                      <span>
                        Stock disponible: {Number(effectiveStock).toLocaleString('es-AR')}
                        {hasTalles && selectedSize ? ` (Talle ${selectedSize.nombre})` : ''}
                      </span>
                    </strong>
                  ) : (
                    <strong className="text-danger d-inline-flex align-items-center gap-1">
                      <i className="bi bi-x-circle-fill"></i>
                      <span>Sin stock{hasTalles && selectedSize ? ` en talle ${selectedSize.nombre}` : ''}</span>
                    </strong>
                  )}
                </div>
              )}
            </div>

            {/* Selector de Talle / Medida */}
            {hasTalles && (
              <div className="mb-3">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <label className="form-label small fw-bold text-muted text-uppercase mb-0">
                    Seleccionar Talle / Medida: <span className="text-dark">{selectedSize?.nombre}</span>
                  </label>
                  {selectedSize && (
                    <span className="small text-muted" style={{ fontSize: '0.78rem' }}>
                      {Number(selectedSize.stock || 0) > 0 ? (
                        <span className="text-success fw-semibold"><i className="bi bi-check-circle me-1"></i>{Number(selectedSize.stock)} en stock</span>
                      ) : (
                        <span className="text-danger fw-bold"><i className="bi bi-x-circle me-1"></i>Sin stock</span>
                      )}
                    </span>
                  )}
                </div>
                <div className="d-flex flex-wrap gap-2">
                  {talles.map((t) => {
                    const isSelected = selectedSize?.id === t.id || selectedSize?.nombre === t.nombre;
                    const sizeStock = Number(t.stock || 0);
                    const isSizeInStock = sizeStock > 0;

                    return (
                      <button
                        key={t.id || t.nombre}
                        type="button"
                        className={`btn btn-sm rounded-3 px-2.5 py-1.5 fw-semibold d-inline-flex align-items-center justify-content-center gap-1.5 ${
                          isSelected
                            ? 'btn-primary shadow-sm'
                            : isSizeInStock
                            ? 'btn-outline-secondary border-light-subtle bg-light text-dark'
                            : 'btn-light text-muted border text-decoration-line-through opacity-60'
                        }`}
                        onClick={() => setSelectedSize(t)}
                        title={isSizeInStock ? `Talle ${t.nombre} (Stock: ${sizeStock})` : `Talle ${t.nombre} (Sin stock)`}
                      >
                        <span>{t.nombre}</span>
                        {isLoggedIn && (
                          <span 
                            className={`badge rounded-pill ms-1 ${isSelected ? 'bg-white text-primary' : isSizeInStock ? 'bg-secondary-subtle text-secondary' : 'bg-danger-subtle text-danger'}`} 
                            style={{ fontSize: '0.7rem', padding: '0.2em 0.5em', lineHeight: 1 }}
                          >
                            {sizeStock}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Variedades / Colores */}
            {variants.length > 1 && (
              <div className="mb-3">
                <label className="form-label small fw-bold text-muted text-uppercase mb-2">
                  Seleccionar Variedad / Color: <span className="text-dark">{selectedVariant?.name}</span>
                </label>
                <div className="d-flex flex-wrap gap-2">
                  {variants.map((v) => {
                    if (v.color || v.isRainbow || v.isTransparent) {
                      let dotClass = "ecom-detail-color-swatch-dot";
                      if (v.isRainbow) dotClass += " ecom-color-swatch-rainbow";
                      if (v.isTransparent) dotClass += " ecom-color-swatch-trans";
                      return (
                        <button
                          key={v.id}
                          type="button"
                          className={`ecom-detail-color-swatch ${selectedVariant?.id === v.id ? 'selected' : ''}`}
                          onClick={() => setSelectedVariant(v)}
                          title={v.name}
                          aria-label={v.name}
                        >
                          <span
                            className={dotClass}
                            style={{
                              backgroundColor: v.color || '#ffffff'
                            }}
                          />
                        </button>
                      );
                    }
                    return (
                      <button
                        key={v.id}
                        type="button"
                        className={`btn btn-sm rounded-pill px-3 fw-semibold ${
                          selectedVariant?.id === v.id
                            ? 'btn-primary shadow-sm'
                            : 'btn-outline-secondary'
                        }`}
                        onClick={() => setSelectedVariant(v)}
                      >
                        {v.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Precio */}
            <div className="p-3 bg-white border rounded-3 mb-3">
              {effectiveCanSeePrice ? (
                (() => {
                  const offerPrice = Number(product?.i_preant ?? product?.I_PREANT ?? 0);
                  const isOffer = offerPrice > 0;
                  const finalPrice = isOffer ? offerPrice : effectivePrice;
                  const listPriceToStrike = Number(effectiveRegularPrice || product?.i_conta || product?.i_lista || effectivePrice || 0);
                  return (
                    <div>
                      {isOffer && (
                        <div className="d-flex align-items-center gap-2 mb-1">
                          <span className="text-muted text-decoration-line-through fw-semibold" style={{ fontSize: '0.95rem' }}>
                            ${listPriceToStrike.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                          <span className="badge bg-danger-subtle text-danger border border-danger-subtle px-2 py-0.5 rounded-1 fw-bold" style={{ fontSize: '0.72rem' }}>
                            LISTA
                          </span>
                        </div>
                      )}
                      <span className="small text-muted d-block">
                        {isOffer ? 'Precio promocional' : 'Precio unitario'} {priceListLabel && !isOffer ? `(${priceListLabel})` : ''}:
                      </span>
                      <div className="d-flex align-items-baseline gap-2">
                        <span className={`fs-3 fw-bolder ${isOffer ? 'text-danger' : 'text-primary'}`}>
                          ${Number(finalPrice || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                        <span className="small text-muted">s/IVA</span>
                      </div>
                    </div>
                  );
                })()
              ) : (
                <div className="d-flex align-items-center justify-content-between">
                  <div>
                    <span className="fw-bold text-dark d-block">Precios exclusivos para clientes</span>
                    <span className="small text-muted">Registrate o iniciá sesión para ver tu lista asignada</span>
                  </div>
                  <button className="btn btn-warning btn-sm fw-bold rounded-pill px-3 shadow-sm" onClick={onLoginClick}>
                    <i className="bi bi-person-plus-fill me-1"></i> Ver Precios
                  </button>
                </div>
              )}
            </div>

            {/* Cantidad y Agregar */}
            <div className="d-flex align-items-center gap-3">
              <div className={`ecom-qty-picker ecom-qty-picker-lg ${!hasStock ? 'opacity-50 pe-none' : ''}`}>
                <button
                  type="button"
                  className="ecom-qty-btn"
                  onClick={() => setQuantity((prev) => (prev > 1 ? prev - 1 : 1))}
                  title="Disminuir"
                  disabled={!hasStock || quantity <= 1}
                >
                  <i className="bi bi-dash fs-5"></i>
                </button>
                <input
                  type="text"
                  className="ecom-qty-input"
                  value={hasStock ? quantity : 0}
                  disabled={!hasStock}
                  onChange={(e) => {
                    const val = parseInt(e.target.value, 10);
                    if (isNaN(val) || val < 1) {
                      setQuantity(1);
                    } else if (effectiveStock > 0 && val > effectiveStock) {
                      setQuantity(effectiveStock);
                    } else {
                      setQuantity(val);
                    }
                  }}
                />
                <button
                  type="button"
                  className="ecom-qty-btn"
                  onClick={() => setQuantity((prev) => (effectiveStock > 0 && prev >= effectiveStock ? effectiveStock : prev + 1))}
                  title="Aumentar"
                  disabled={!hasStock || (effectiveStock > 0 && quantity >= effectiveStock)}
                >
                  <i className="bi bi-plus fs-5"></i>
                </button>
              </div>

              <Button
                variant={!hasStock ? 'secondary' : !isLoggedIn ? 'warning' : isAdded ? 'success' : 'primary'}
                className={`flex-grow-1 py-2 rounded-3 fw-bold d-flex align-items-center justify-content-center gap-2 shadow-sm ${
                  !hasStock ? 'disabled opacity-75' : ''
                }`}
                onClick={
                  !hasStock
                    ? undefined
                    : !isLoggedIn
                    ? onLoginClick
                    : handleAddToCart
                }
                disabled={!hasStock}
                title={!hasStock ? 'Artículo sin stock disponible' : !isLoggedIn ? 'Iniciá sesión para comprar' : 'Agregar al carrito'}
              >
                {!hasStock ? (
                  <>
                    <i className="bi bi-slash-circle fs-5"></i> Sin stock disponible{hasTalles && selectedSize ? ` (Talle ${selectedSize.nombre})` : ''}
                  </>
                ) : !isLoggedIn ? (
                  <>
                    <i className="bi bi-person-lock fs-5"></i> Ingresá para comprar
                  </>
                ) : isAdded ? (
                  <>
                    <i className="bi bi-check-lg fs-5"></i> ¡Agregado al Carrito!
                  </>
                ) : (
                  <>
                    <i className="bi bi-cart-plus-fill fs-5"></i> Agregar al Carrito
                  </>
                )}
              </Button>
            </div>

          </div>

          {/* SECCIÓN DE ARTÍCULOS RELACIONADOS */}
          {relatedConfig.enabled !== false && relatedProducts.length > 0 && (
            <div className="col-12 mt-4 pt-4 border-top">
              <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
                <div>
                  <h5 className="fw-bold text-dark mb-0 d-flex align-items-center gap-2">
                    <i className="bi bi-boxes text-primary"></i>
                    <span>{relatedConfig.title || 'Artículos Relacionados'}</span>
                  </h5>
                  {relatedConfig.subtitle && (
                    <small className="text-muted">{relatedConfig.subtitle}</small>
                  )}
                </div>
                <span className="badge bg-light text-muted border px-2.5 py-1.5 fw-semibold">
                  {relatedProducts.length} {relatedProducts.length === 1 ? 'producto sugerido' : 'productos sugeridos'}
                </span>
              </div>

              <div className="row g-3 row-cols-2 row-cols-sm-3 row-cols-md-4 row-cols-lg-6">
                {relatedProducts.map((relArt) => (
                  <div key={relArt.c_artic || relArt.id} className="col">
                    <RelatedProductCard
                      product={relArt}
                      canSeePrice={effectiveCanSeePrice}
                      getPrice={getPriceForArticle}
                      onClick={() => handleSelectRelated(relArt)}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

        </div>
      </Modal.Body>
    </Modal>

    {/* Modal Lightbox de Imagen en Grande */}
    {hasImage && (
      <Modal
        show={isLightboxOpen}
        onHide={() => setIsLightboxOpen(false)}
        centered
        fullscreen
        className="ecom-lightbox-modal"
        contentClassName="bg-black bg-opacity-95 border-0 rounded-0"
      >
        <div className="d-flex flex-column h-100 position-relative p-3 p-md-4 text-white">
          {/* Cabecera del Lightbox */}
          <div className="d-flex align-items-center justify-content-between mb-2 z-3">
            <div className="text-truncate me-3 d-flex align-items-center flex-wrap gap-2">
              <span className="badge bg-primary font-monospace px-2.5 py-1">{product.c_artic}</span>
              <span className="fw-bold fs-6 text-white text-truncate">{product.d_artic}</span>
              {totalValidSlides > 1 && (
                <span className="badge bg-secondary bg-opacity-75">
                  Foto {validSlides.findIndex(s => s.index === currentSlide?.index) + 1} de {totalValidSlides}
                </span>
              )}
            </div>
            <button
              type="button"
              className="btn btn-outline-light rounded-circle d-flex align-items-center justify-content-center border-2 shadow"
              style={{ width: '42px', height: '42px', flexShrink: 0, opacity: 0.9 }}
              onClick={() => setIsLightboxOpen(false)}
              title="Cerrar visor (Esc)"
              aria-label="Cerrar visor"
            >
              <i className="bi bi-x-lg fs-5"></i>
            </button>
          </div>

          {/* Área Central de la Imagen Ampliada */}
          <div className="flex-grow-1 position-relative d-flex align-items-center justify-content-center overflow-hidden my-auto p-2">
            <img
              key={`lightbox_${currentSlideIndex}_${currentUrl}`}
              src={currentUrl}
              alt={`${product.d_artic} - Foto Grande ${currentSlideIndex + 1}`}
              className="img-fluid rounded-3 shadow-lg"
              style={{
                maxHeight: totalValidSlides > 1 ? 'calc(100vh - 170px)' : 'calc(100vh - 110px)',
                maxWidth: '95vw',
                objectFit: 'contain',
                userSelect: 'none'
              }}
              onError={() => handleImageError(currentSlide.index)}
            />

            {/* Flechas de Navegación en el Lightbox */}
            {totalValidSlides > 1 && (
              <>
                <button
                  type="button"
                  className="btn btn-dark bg-opacity-75 border border-secondary position-absolute top-50 start-0 translate-middle-y ms-2 ms-md-4 rounded-circle d-flex align-items-center justify-content-center shadow"
                  style={{ width: '48px', height: '48px', zIndex: 10 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePrev();
                  }}
                  title="Foto anterior (←)"
                  aria-label="Foto anterior"
                >
                  <i className="bi bi-chevron-left fs-4"></i>
                </button>
                <button
                  type="button"
                  className="btn btn-dark bg-opacity-75 border border-secondary position-absolute top-50 end-0 translate-middle-y me-2 me-md-4 rounded-circle d-flex align-items-center justify-content-center shadow"
                  style={{ width: '48px', height: '48px', zIndex: 10 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleNext();
                  }}
                  title="Foto siguiente (→)"
                  aria-label="Foto siguiente"
                >
                  <i className="bi bi-chevron-right fs-4"></i>
                </button>
              </>
            )}
          </div>

          {/* Miniaturas inferiores en el Lightbox si hay más de una foto */}
          {totalValidSlides > 1 && (
            <div className="d-flex align-items-center justify-content-center gap-2 pt-2 z-3 overflow-auto">
              {validSlides.map((slide) => {
                const thumbUrl = getSlideUrl(slide);
                if (!thumbUrl) return null;
                const isActive = slide.index === currentSlide?.index;
                return (
                  <button
                    key={slide.index}
                    type="button"
                    className={`btn p-0.5 rounded-2 border ${
                      isActive ? 'border-primary border-2 shadow' : 'border-secondary opacity-60'
                    }`}
                    style={{
                      width: '56px',
                      height: '56px',
                      background: '#1e293b',
                      cursor: 'pointer',
                      transform: isActive ? 'scale(1.08)' : 'scale(1)',
                      transition: 'all 0.2s ease'
                    }}
                    onClick={() => {
                      const origIdx = slides.findIndex(s => s.index === slide.index);
                      if (origIdx !== -1) setCurrentSlideIndex(origIdx);
                    }}
                    title={`Ver foto ${validSlides.indexOf(slide) + 1}`}
                  >
                    <img
                      src={thumbUrl}
                      alt={`Miniatura ${validSlides.indexOf(slide) + 1}`}
                      className="w-100 h-100 rounded-1"
                      style={{ objectFit: 'cover' }}
                      onError={() => handleImageError(slide.index)}
                    />
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </Modal>
    )}
  </>
  );
};

export default EcommerceProductDetail;
